import React from 'react';
import {
  NO_PRESSES,
  isTapKey,
  pressEnds,
  pressStarts,
  type TapChange,
  type TapPresses,
} from 'Utils/playAlong/tapInput';

export interface TapInputOptions {
  /** Taps are played now (an exercise that takes taps is on screen and running). */
  listening: boolean;
  /** The exercise area: a press here is a tap; a press anywhere else is not. */
  area: React.RefObject<HTMLElement | null>;
  /** A tap went down: the tap note starts. */
  noteOn: () => void;
  /** The tap came up: the tap note ends. */
  noteOff: () => void;
}

const holdsAModifier = (event: MouseEvent | KeyboardEvent): boolean =>
  event.shiftKey || event.ctrlKey || event.altKey || event.metaKey;

/**
 * Tap input for the tutorial exercises played with any key (see
 * Utils/playAlong/tapInput). While `listening`, a press on the exercise area
 * (touch, pen or the main mouse button) or a computer key is a key going
 * down, and its release the key coming up. Nothing is listened for otherwise.
 */
export function useTapInput({ listening, area, noteOn, noteOff }: TapInputOptions): void {
  const noteOnRef = React.useRef(noteOn);
  const noteOffRef = React.useRef(noteOff);
  noteOnRef.current = noteOn;
  noteOffRef.current = noteOff;

  React.useEffect(() => {
    const element = area.current;
    if (!listening || !element) return;

    let presses: TapPresses = NO_PRESSES;
    const apply = ([change, next]: [TapChange, TapPresses]) => {
      presses = next;
      if (change === 'retrigger' || change === 'noteOff') noteOffRef.current();
      if (change === 'retrigger' || change === 'noteOn') noteOnRef.current();
    };
    const releaseAll = () => {
      if (presses.sounding !== null) apply(pressEnds(presses, presses.sounding));
    };
    const keyPress = (event: KeyboardEvent) => `key:${event.code || event.key}`;
    const pointerPress = (event: PointerEvent) => `pointer:${event.pointerId}`;

    const onKeyDown = (event: KeyboardEvent) => {
      if (!isTapKey(event)) return;
      // The space bar plays here; it must not also scroll the page.
      if (event.key === ' ') event.preventDefault();
      apply(pressStarts(presses, keyPress(event)));
    };
    const onKeyUp = (event: KeyboardEvent) => apply(pressEnds(presses, keyPress(event)));
    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (holdsAModifier(event)) return;
      apply(pressStarts(presses, pointerPress(event)));
    };
    const onPointerUp = (event: PointerEvent) => apply(pressEnds(presses, pointerPress(event)));
    // A long press must not open a menu or a callout in the middle of a held note.
    const onContextMenu = (event: Event) => event.preventDefault();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') releaseAll();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    element.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    element.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('blur', releaseAll);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      element.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      element.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('blur', releaseAll);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      releaseAll();
    };
  }, [listening, area]);
}
